import * as Popover from "@radix-ui/react-popover";
import { isValid, parseISO } from "date-fns";
import { CalendarDays, Check, ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DateRangeCalendar from "./DateRangeCalendar";
import {
  type DateRangePreset,
  dashboardDateFormatter,
  lastDays,
  lastThirtyDays,
  rollingDays,
} from "./dateSelection";
export { lastThirtyDays, type DateRangePreset } from "./dateSelection";

export default function DashboardDateRange({
  from,
  to,
  onChange,
  preset: selectedPreset,
  rememberedCustomRange,
  pill = false,
  modal = false,
  allowAllTime = true,
  label: accessibleLabel,
  previousPeriod,
  activityUserIds,
}: {
  activityUserIds?: string[];
  pill?: boolean;
  /** Own the scroll lock when opened inside a dialog. */
  modal?: boolean;
  allowAllTime?: boolean;
  label?: string;
  from: string;
  to: string;
  onChange: (from: string, to: string, preset: DateRangePreset) => void;
  preset?: DateRangePreset;
  rememberedCustomRange?: { from: string; to: string };
  previousPeriod?: { from: string; to: string; days: number };
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState({ from, to });
  const range = lastThirtyDays();
  const matchingDays = [30, 90].find(days => {
    const dates = lastDays(days);
    return from === dates.from && to === dates.to;
  });
  const preset =
    selectedPreset ?? (!from && !to ? "all" : matchingDays ? `${matchingDays}d` : "custom");
  const previousSelected =
    !!previousPeriod && from === previousPeriod.from && to === previousPeriod.to;
  const activePreset = previousSelected ? "previous" : preset;
  const [lastCustomRange, setLastCustomRange] = useState<{
    from: string;
    to: string;
  } | null>(() => (preset === "custom" ? { from, to } : (rememberedCustomRange ?? null)));
  useEffect(() => {
    // Also remember custom dates restored from the URL through Back/Forward.
    if (preset === "custom") setLastCustomRange({ from, to });
  }, [preset, from, to]);
  const valid =
    !!draft.from &&
    !!draft.to &&
    isValid(parseISO(draft.from)) &&
    isValid(parseISO(draft.to)) &&
    draft.from <= draft.to;
  const displayDate = dashboardDateFormatter(
    [from, to, draft.from, draft.to],
    i18n?.resolvedLanguage,
  );
  const label = previousSelected
    ? t("dashboard.previous_n_days", { count: previousPeriod.days })
    : preset === "all"
      ? t("dashboard.all_time")
      : preset === "30d"
        ? t("dashboard.last_thirty_days")
        : rollingDays(preset)
          ? t("dashboard.last_n_days", { count: rollingDays(preset)! })
          : from && to
            ? `${displayDate(from)} – ${displayDate(to)}`
            : `${t(from ? "from" : "to")} ${displayDate(from || to)}`;

  const apply = (nextFrom: string, nextTo: string, nextPreset: DateRangePreset) => {
    if (nextPreset === "custom") setLastCustomRange({ from: nextFrom, to: nextTo });
    onChange(nextFrom, nextTo, nextPreset);
    setOpen(false);
  };

  return (
    <Popover.Root
      modal={modal}
      open={open}
      onOpenChange={nextOpen => {
        if (nextOpen) {
          const dates = preset === "custom" ? { from, to } : lastCustomRange;
          setDraft({
            from: dates?.from || range.from,
            to: dates?.to || range.to,
          });
          setCustom(preset === "custom" && !previousSelected);
        }
        setOpen(nextOpen);
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`${accessibleLabel ?? t("dashboard.date_range")}: ${label}`}
          className={`dropdown-trigger ${pill ? "h-auto max-w-full gap-1 rounded-full px-1.5 py-1 text-xs" : ""}`}
        >
          <CalendarDays
            aria-hidden="true"
            className={`${pill ? "h-3 w-3" : "h-4 w-4"} shrink-0 text-text-dim`}
          />
          <span className="min-w-0 truncate">{label}</span>
          <ChevronDown
            aria-hidden="true"
            className={`${pill ? "h-3 w-3" : "h-4 w-4"} shrink-0 text-text-dim`}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          side={modal ? "top" : "bottom"}
          sideOffset={6}
          collisionPadding={16}
          aria-label={accessibleLabel ?? t("dashboard.date_range")}
          className={`dropdown-menu max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-32px)] overflow-y-auto ${modal ? "pd-theme z-[120]" : ""} ${custom ? "w-[760px]" : "w-56 min-w-[var(--radix-popover-trigger-width)]"}`}
        >
          <div className={custom ? "flex flex-col sm:flex-row" : ""}>
            <div
              className={
                custom
                  ? "grid shrink-0 grid-cols-2 sm:block sm:w-44 sm:border-r sm:border-outline sm:pr-2"
                  : ""
              }
            >
              {[
                { id: "all", label: t("dashboard.all_time") },
                { id: "30d", label: t("dashboard.last_thirty_days") },
                { id: "90d", label: t("dashboard.last_n_days", { count: 90 }) },
                ...(previousPeriod
                  ? [
                      {
                        id: "previous",
                        label: t("dashboard.previous_n_days", { count: previousPeriod.days }),
                        detail: `${displayDate(previousPeriod.from)} – ${displayDate(previousPeriod.to)}`,
                      },
                    ]
                  : []),
                { id: "custom", label: t("dashboard.custom_range") },
              ]
                .filter(option => allowAllTime || option.id !== "all")
                .map(option => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={(custom ? "custom" : activePreset) === option.id}
                    onClick={() => {
                      if (option.id === "custom") setCustom(true);
                      else if (option.id === "all") apply("", "", "all");
                      else if (option.id === "previous" && previousPeriod)
                        apply(previousPeriod.from, previousPeriod.to, "custom");
                      else {
                        const next = lastDays(rollingDays(option.id)!);
                        apply(next.from, next.to, option.id as DateRangePreset);
                      }
                    }}
                    className="dropdown-option w-full pl-8 aria-pressed:bg-muted-surface"
                  >
                    <span>
                      {option.label}
                      {"detail" in option && (
                        <span className="mt-0.5 block text-xs text-text-dim">{option.detail}</span>
                      )}
                    </span>
                    {(custom ? "custom" : activePreset) === option.id && (
                      <Check aria-hidden="true" className="dropdown-check" />
                    )}
                  </button>
                ))}
              {!custom && (rollingDays(preset) || previousSelected) && (
                <p className="mt-2 border-t border-outline px-3 pt-3 pb-2 text-xs text-text-dim">
                  {displayDate(from)} – {displayDate(to)}
                </p>
              )}
            </div>
            {custom && (
              <form
                className="min-w-0 flex-1 space-y-3 border-t border-outline p-3 sm:border-t-0 sm:pl-5"
                onSubmit={event => {
                  event.preventDefault();
                  if (valid) apply(draft.from, draft.to, "custom");
                }}
              >
                <DateRangeCalendar
                  activityUserIds={activityUserIds}
                  from={draft.from}
                  to={draft.to}
                  onChange={(from, to) => setDraft({ from, to })}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-outline pt-3">
                  <span className="text-xs text-text-mid" aria-live="polite">
                    {valid
                      ? `${displayDate(draft.from)} – ${displayDate(draft.to)}`
                      : t("dashboard.select_date_range")}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded-lg px-3 py-2 text-sm text-text-mid hover:bg-muted-surface"
                      onClick={() => setOpen(false)}
                    >
                      {t("cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={!valid}
                      className="rounded-lg bg-info px-3 py-2 text-sm font-medium text-white transition hover:opacity-90 focus-visible:outline-info disabled:opacity-40"
                    >
                      {t("scope_filter_apply")}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
